/* gt-b2b.js — B2B partnership page (sections/gt-b2b-partnership.liquid).
 *
 * - Customer / Supplier mode switch (ARIA tabs, automatic activation, arrow keys).
 * - Clicking a mode card (mouse, touch, Enter/Space) — even the already-active one —
 *   smoothly scrolls to the start of that mode's application form, below the sticky
 *   header. Arrow-key browsing between the cards and page-load deep links never scroll.
 * - Client-side validation with inline, screen-reader-linked errors.
 * - Native form POST to Shopify (/contact). Success / failure is decided ONLY by
 *   Shopify's response, rendered server-side (form.posted_successfully? / form.errors).
 * - Double-submit guard; per-mode draft kept in sessionStorage so switching modes,
 *   a server error or a spam-challenge round-trip never wipes what was typed.
 * - On a confirmed success, publishes one "gt:lead" event through gt-lead-events.js
 *   (fail-open, no-op when analytics is absent).
 * Event handling is document-level delegation; guarded against double inclusion.
 */
(function () {
  if (window.__gtB2bInit) return;
  window.__gtB2bInit = true;

  var MODES = ['customer', 'supplier'];
  var PENDING_KEY = 'gtB2bPending';
  var DRAFT_PREFIX = 'gtB2bDraft:';

  /* ---------- storage (never throws) ---------- */
  function store(method, key, value) {
    try {
      var s = window.sessionStorage;
      if (method === 'get') return s.getItem(key);
      if (method === 'set') s.setItem(key, value);
      if (method === 'remove') s.removeItem(key);
    } catch (e) {
      /* private mode / blocked storage: drafts simply aren't kept */
    }
    return null;
  }

  function i18n(root) {
    if (root.__gtB2bI18n) return root.__gtB2bI18n;
    var data = {};
    try {
      var node = root.querySelector('[data-gt-b2b-i18n]');
      if (node) data = JSON.parse(node.textContent);
    } catch (e) {
      data = {};
    }
    root.__gtB2bI18n = data;
    return data;
  }

  function card(root, mode) {
    return root.querySelector('[data-gt-b2b-form="' + mode + '"]');
  }
  function formOf(root, mode) {
    var c = card(root, mode);
    return c ? c.querySelector('form') : null;
  }
  function modeOfForm(form) {
    var c = form && form.closest('[data-gt-b2b-form]');
    return c ? c.getAttribute('data-gt-b2b-form') : null;
  }

  /* ---------- mode switching ---------- */
  function select(root, mode, opts) {
    opts = opts || {};
    if (MODES.indexOf(mode) === -1) mode = 'customer';
    MODES.forEach(function (m) {
      var tab = root.querySelector('[data-gt-b2b-tab="' + m + '"]');
      var panel = root.querySelector('[data-gt-b2b-panel="' + m + '"]');
      var on = m === mode;
      if (tab) {
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      }
      if (panel) {
        if (on) {
          if (panel.hidden) {
            panel.hidden = false;
            panel.classList.remove('is-entering');
            void panel.offsetWidth; // restart the fade
            panel.classList.add('is-entering');
          }
        } else {
          panel.hidden = true;
          panel.classList.remove('is-entering');
        }
      }
    });
    root.setAttribute('data-gt-b2b-mode', mode);
    if (opts.announce) {
      var live = root.querySelector('[data-gt-b2b-live]');
      if (live) live.textContent = i18n(root)[mode] || '';
    }
    if (opts.updateUrl) {
      try {
        var url = window.location.pathname + window.location.search + (mode === 'supplier' ? '#suppliers' : '#customers');
        window.history.replaceState(window.history.state, '', url);
      } catch (e) {
        /* ignore */
      }
    }
  }

  /* ---------- scroll to a mode's form ---------- */
  var SCROLL_GAP = 12; // px of air between the sticky header and the form card

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /* Height currently covered by the theme's sticky header (0 when it is static or
     hidden). Measured at scroll time because "reduce-logo-size" changes it. */
  function stickyHeaderOffset() {
    var h = document.querySelector('.section-header');
    if (!h) return 0;
    var pos = window.getComputedStyle(h).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    if (h.classList.contains('shopify-section-header-hidden')) return 0;
    return Math.max(0, Math.round(h.getBoundingClientRect().height));
  }

  /* Exact document Y of an element, minus the vertical offset of the panel's
     fade-in transform (if it is still running) so the target is the final position. */
  function docTop(el) {
    var y = el.getBoundingClientRect().top + window.pageYOffset;
    var panel = el.closest('[data-gt-b2b-panel]');
    if (panel) {
      try {
        var t = window.getComputedStyle(panel).transform;
        if (t && t !== 'none') y -= new DOMMatrixReadOnly(t).m42;
      } catch (e) {
        /* ignore */
      }
    }
    return y;
  }

  function scrollToForm(root, mode, opts) {
    opts = opts || {};
    var target = card(root, mode);
    if (!target) return;
    // wait one frame so the just-unhidden panel has its final layout
    window.requestAnimationFrame(function () {
      var top = Math.max(0, Math.round(docTop(target) - stickyHeaderOffset() - SCROLL_GAP));
      var behavior = opts.instant || prefersReducedMotion() ? 'instant' : 'smooth';
      try {
        window.scrollTo({ top: top, behavior: behavior });
      } catch (e) {
        window.scrollTo(0, top); // very old browsers: no options object
      }
    });
  }

  function modeFromUrl() {
    var h = (window.location.hash || '').toLowerCase();
    if (h.indexOf('supplier') !== -1) return 'supplier';
    if (h.indexOf('customer') !== -1) return 'customer';
    try {
      var p = new URLSearchParams(window.location.search).get('for');
      if (p && p.toLowerCase().indexOf('supplier') === 0) return 'supplier';
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  /* ---------- multi-select categories → one hidden field ---------- */
  function syncMulti(form) {
    var target = form.querySelector('[data-gt-b2b-multi-target]');
    if (!target) return;
    var vals = [];
    form.querySelectorAll('[data-gt-b2b-multi]').forEach(function (cb) {
      if (cb.checked) vals.push(cb.value);
    });
    target.value = vals.join('; ');
  }

  /* ---------- drafts ---------- */
  function saveDraft(form) {
    var mode = modeOfForm(form);
    if (!mode) return;
    var data = { v: {}, multi: [] };
    form.querySelectorAll('input, select, textarea').forEach(function (el) {
      if (!el.name || el.type === 'hidden' || el.hasAttribute('data-gt-b2b-consent')) return;
      if (el.type === 'radio') {
        if (el.checked) data.v[el.name] = el.value;
        return;
      }
      if (el.type === 'checkbox') return;
      if (el.value) data.v[el.name] = el.value;
    });
    form.querySelectorAll('[data-gt-b2b-multi]').forEach(function (cb) {
      if (cb.checked) data.multi.push(cb.value);
    });
    store('set', DRAFT_PREFIX + mode, JSON.stringify(data));
  }

  function restoreDraft(form) {
    var mode = modeOfForm(form);
    var raw = store('get', DRAFT_PREFIX + mode);
    if (!raw) return;
    var data;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!data || !data.v) return;
    form.querySelectorAll('input, select, textarea').forEach(function (el) {
      if (!el.name || el.type === 'hidden' || !(el.name in data.v)) return;
      if (el.type === 'radio') {
        el.checked = el.value === data.v[el.name];
      } else if (el.type !== 'checkbox') {
        el.value = data.v[el.name];
      }
    });
    form.querySelectorAll('[data-gt-b2b-multi]').forEach(function (cb) {
      cb.checked = (data.multi || []).indexOf(cb.value) !== -1;
    });
    syncMulti(form);
  }

  /* ---------- validation ---------- */
  var RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var RE_PHONE_CHARS = /^[+\d\s().\-]+$/;
  var RE_URL = /^(https?:\/\/)?[^\s\/.]+(\.[^\s\/.]+)+(\/\S*)?$/i;

  function messageFor(el, t) {
    var v = (el.value || '').trim();
    if (el.type === 'checkbox') {
      return el.required && !el.checked ? t.consent : '';
    }
    if (!v) {
      if (!el.required) return '';
      return el.tagName === 'SELECT' ? t.select : t.required;
    }
    if (el.type === 'email' && !RE_EMAIL.test(v)) return t.email;
    if (el.type === 'tel') {
      var digits = v.replace(/\D/g, '').length;
      if (!RE_PHONE_CHARS.test(v) || digits < 9 || digits > 15) return t.phone;
    }
    if (el.type === 'url' && !RE_URL.test(v)) return t.url;
    return '';
  }

  function setError(el, msg) {
    var field = el.closest('[data-gt-b2b-field]');
    var err = document.getElementById(el.id + '-error');
    if (msg) {
      el.setAttribute('aria-invalid', 'true');
      if (field) field.classList.add('is-invalid');
      if (err) {
        err.textContent = msg;
        err.hidden = false;
      }
    } else {
      el.removeAttribute('aria-invalid');
      if (field) field.classList.remove('is-invalid');
      if (err) {
        err.textContent = '';
        err.hidden = true;
      }
    }
  }

  function validatable(form) {
    return Array.prototype.filter.call(form.querySelectorAll('input, select, textarea'), function (el) {
      return el.id && el.type !== 'hidden' && el.type !== 'radio' && !el.hasAttribute('data-gt-b2b-multi');
    });
  }

  function validate(form, t) {
    var first = null;
    validatable(form).forEach(function (el) {
      var msg = messageFor(el, t);
      setError(el, msg);
      if (msg && !first) first = el;
    });
    return first;
  }

  /* ---------- submit state ---------- */
  function setSubmitting(form, on, t) {
    var btn = form.querySelector('[data-gt-b2b-submit]');
    var label = form.querySelector('[data-gt-b2b-submit-label]');
    if (on) {
      form.setAttribute('data-submitting', 'true');
      form.setAttribute('aria-busy', 'true');
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
      }
      if (label) {
        label.setAttribute('data-label', label.textContent);
        if (t.sending) label.textContent = t.sending;
      }
    } else {
      form.removeAttribute('data-submitting');
      form.removeAttribute('aria-busy');
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
      if (label && label.getAttribute('data-label')) label.textContent = label.getAttribute('data-label');
    }
  }

  function showFields(c) {
    var success = c.querySelector('[data-gt-b2b-success]');
    var fields = c.querySelector('[data-gt-b2b-fields]');
    var serverErr = c.querySelector('[data-gt-b2b-server-error]');
    if (success) success.hidden = true;
    if (fields) {
      fields.hidden = false;
      fields.setAttribute('data-gt-b2b-posted', '');
    }
    if (serverErr) serverErr.hidden = true;
  }

  function cleanPostedUrl() {
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.has('contact_posted')) {
        url.searchParams.delete('contact_posted');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {
      /* ignore */
    }
  }

  /* Focus a status message once the browser has finished its own load-time
     scrolling (fragment navigation can otherwise steal focus back). */
  function focusLater(el) {
    if (!el) return;
    var run = function () {
      setTimeout(function () {
        try {
          el.focus({ preventScroll: true });
        } catch (e) {
          el.focus();
        }
        var cardEl = el.closest('[data-gt-b2b-form]');
        var root = el.closest('[data-gt-b2b]');
        if (cardEl && root) scrollToForm(root, cardEl.getAttribute('data-gt-b2b-form'), { instant: true });
      }, 0);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  /* ---------- init ---------- */
  function init(root) {
    if (!root || root.__gtB2bReady) return;
    root.__gtB2bReady = true;
    root.classList.add('gt-b2b--js');

    var pending = store('get', PENDING_KEY);
    var posted = {};
    MODES.forEach(function (m) {
      var c = card(root, m);
      var f = c && c.querySelector('[data-gt-b2b-fields]');
      posted[m] = f ? f.getAttribute('data-gt-b2b-posted') || '' : '';
    });
    var anyPosted = posted.customer || posted.supplier;

    var mode;
    if (anyPosted) {
      // Both forms are Shopify 'contact' forms, so both receive the posted state.
      // Keep it only on the one that was actually submitted.
      mode = MODES.indexOf(pending) !== -1 ? pending : modeFromUrl() || 'customer';
      MODES.forEach(function (m) {
        if (m !== mode) showFields(card(root, m));
      });
    } else {
      mode = modeFromUrl() || 'customer';
    }
    select(root, mode, { announce: false, updateUrl: false });

    MODES.forEach(function (m) {
      var f = formOf(root, m);
      if (!f) return;
      if (anyPosted && m === mode && posted[m] === 'success') {
        store('remove', DRAFT_PREFIX + m);
        f.reset();
        syncMulti(f);
      } else {
        restoreDraft(f);
      }
    });

    if (anyPosted) {
      var c = card(root, mode);
      if (posted[mode] === 'success') {
        if (pending === mode) {
          try {
            if (typeof window.gtEmitLead === 'function') {
              window.gtEmitLead('b2b_application', { application_type: mode });
            }
          } catch (e) {
            /* fail-open */
          }
        }
        focusLater(c && c.querySelector('[data-gt-b2b-success]'));
      } else if (posted[mode] === 'error') {
        focusLater(c && c.querySelector('[data-gt-b2b-server-error]'));
      }
      store('remove', PENDING_KEY);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-gt-b2b]').forEach(init);
  }

  /* ---------- delegated events ---------- */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('[data-gt-b2b-tab]');
    if (tab) {
      var root = tab.closest('[data-gt-b2b]');
      if (root) {
        var mode = tab.getAttribute('data-gt-b2b-tab');
        select(root, mode, { announce: true, updateUrl: true });
        scrollToForm(root, mode);
      }
      return;
    }
    // "Fill in the application" shortcut (mobile/tablet): same header-aware scroll
    var jump = e.target.closest && e.target.closest('.gt-b2b__jump');
    if (jump) {
      var jr = jump.closest('[data-gt-b2b]');
      var jp = jump.closest('[data-gt-b2b-panel]');
      if (jr && jp) {
        e.preventDefault();
        scrollToForm(jr, jp.getAttribute('data-gt-b2b-panel'));
      }
      return;
    }
    var again = e.target.closest && e.target.closest('[data-gt-b2b-again]');
    if (again) {
      var c = again.closest('[data-gt-b2b-form]');
      if (!c) return;
      showFields(c);
      var f = c.querySelector('form');
      if (f) {
        f.reset();
        syncMulti(f);
        validatable(f).forEach(function (el) {
          setError(el, '');
        });
        var firstInput = f.querySelector('.gt-b2b__input');
        if (firstInput) firstInput.focus();
      }
      cleanPostedUrl();
    }
  });

  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-gt-b2b-tab]');
    if (!tab) return;
    var root = tab.closest('[data-gt-b2b]');
    if (!root) return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-gt-b2b-tab]'));
    var i = tabs.indexOf(tab);
    var next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    next.focus();
    select(root, next.getAttribute('data-gt-b2b-tab'), { announce: true, updateUrl: true });
  });

  var draftTimer = null;
  function onEdit(e) {
    var el = e.target;
    var form = el.form || (el.closest && el.closest('form'));
    if (!form || !form.closest('[data-gt-b2b]')) return;
    if (el.hasAttribute && el.hasAttribute('data-gt-b2b-multi')) syncMulti(form);
    if (el.getAttribute && el.getAttribute('aria-invalid') === 'true') {
      setError(el, messageFor(el, i18n(form.closest('[data-gt-b2b]'))));
    }
    clearTimeout(draftTimer);
    draftTimer = setTimeout(function () {
      saveDraft(form);
    }, 250);
  }
  document.addEventListener('input', onEdit);
  document.addEventListener('change', onEdit);

  document.addEventListener(
    'blur',
    function (e) {
      var el = e.target;
      if (!el || !el.classList || !el.classList.contains('gt-b2b__input')) return;
      var root = el.closest('[data-gt-b2b]');
      if (!root) return;
      // validate on blur only once the visitor has typed something
      if ((el.value || '').trim()) setError(el, messageFor(el, i18n(root)));
    },
    true
  );

  document.addEventListener('submit', function (e) {
    var form = e.target;
    var root = form && form.closest && form.closest('[data-gt-b2b]');
    if (!root) return;
    var t = i18n(root);
    var summary = form.querySelector('[data-gt-b2b-summary]');

    if (form.getAttribute('data-submitting') === 'true') {
      e.preventDefault(); // second click while the first POST is in flight
      return;
    }
    syncMulti(form);
    var firstInvalid = validate(form, t);
    if (firstInvalid) {
      e.preventDefault();
      if (summary) {
        summary.textContent = t.fix || '';
        summary.hidden = false;
      }
      firstInvalid.focus();
      return;
    }
    if (summary) summary.hidden = true;
    saveDraft(form);
    store('set', PENDING_KEY, modeOfForm(form) || '');
    setSubmitting(form, true, t);
    // no preventDefault: the browser performs the native POST to Shopify
  });

  // Back/forward cache: never leave a form stuck in "sending".
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('[data-gt-b2b] form[data-submitting]').forEach(function (f) {
      setSubmitting(f, false, {});
    });
  });

  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
